import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar, Button, Result, notification } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "../../store";
import DataProvider from "../../services";
import Colors from "../../constants/Colors";
import { formatTime } from "../../utils";
import { CreditCardOutlined } from "@ant-design/icons";

export default function ConfirmMcarePayment() {
  const [api, contextHolder] = notification.useNotification();
  const { plan, user, setUser } = useAppStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<number>(0);
  const [time, setTime] = useState(120); // 2 minutes in seconds
  const [isActive, setIsActive] = useState(true);
  const timeRef = useRef(time);
  timeRef.current = time;
  const pollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user) navigate("/sign-up");
  }, [user, navigate]);

  // Coupon success: show success screen immediately when redirected after coupon activation
  useEffect(() => {
    if (searchParams.get("success") === "coupon") {
      setStatus(1);
      DataProvider.getProfile().then((result) => setUser(result.data)).catch(() => {});
    }
  }, [searchParams, setUser]);

  const checking = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent ?? false;
    try {
      if (!silent) setLoading(true);

      const { data } = await DataProvider.getUserSubscription();

      if (data.message && data.message === "Not a subscriber!") {
        setLoading(false);
        if (!silent) {
          return api["warning"]({
            message: "Warning",
            description:
              "Payment initiation failed. Kindly go back and try again!",
          });
        }
        return;
      }
      if (data && data.status !== "active") {
        setLoading(false);
        setStatus(2);
        return;
      }
      const result = await DataProvider.getProfile();
      setUser(result.data);
      setStatus(1);
      setLoading(false);
      setIsActive(false);
    } catch (error) {
      setLoading(false);
      if (!silent) {
        return api["error"]({
          message: "Error",
          description: "Payment confirmation failed. Kindly try again.",
        });
      }
    }
  }, [api, setUser]);

  /** Stop background polling as soon as the countdown reaches zero. */
  useEffect(() => {
    if (time !== 0) return;
    if (pollIntervalRef.current != null) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [time]);

  /** Poll every 13s while waiting; stops when countdown ends, on success, or leaving this screen. */
  useEffect(() => {
    if (status !== 0) return;
    if (searchParams.get("success") === "coupon") return;

    pollIntervalRef.current = window.setInterval(() => {
      if (timeRef.current <= 0) {
        if (pollIntervalRef.current != null) {
          window.clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return;
      }
      void checking({ silent: true });
    }, 13_000);

    return () => {
      if (pollIntervalRef.current != null) {
        window.clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [status, searchParams, checking]);

  const startCountdown = () => {
    setIsActive(true);
    setTime(120); // Reset time to 2 minutes
  };

  // Update the countdown every second
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setIsActive(false);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]);

  // Automatically start countdown on component mount
  useEffect(() => {
    startCountdown();
  }, []);

  return (
    <div className="flex justify-center items-center w-full h-[85vh]">
      {status === 0 && (
        <Result
          status="info"
          icon={
            <Avatar
              size={64}
              style={{
                backgroundColor: Colors.primaryBlue,
              }}
            >
              <CreditCardOutlined style={{ color: "white", fontSize: 32 }} />
            </Avatar>
          }
          title="Payment has been initiated!"
          subTitle={`A popup of the payment will appear on your device. Kindly confirm with your pin to make payment. If payment popup delays, dial *170# to approve payment.`}
          extra={[
            <Button
              disabled={isActive}
              onClick={() => void checking()}
              style={{ backgroundColor: isActive ? "" : Colors.primaryBlue }}
              type={isActive ? undefined : "primary"}
              key="confirm"
            >
              {isActive
                ? ` Time Remaining: ${formatTime(time)}`
                : loading
                ? "Checking..."
                : "Click Here To Confirm Payment"}
            </Button>,
            <Button
              style={{ color: Colors.primaryBlue }}
              onClick={() =>
                plan && navigate(`/main/billing/${plan.offercode}`)
              }
              key="back"
            >
              Go Back
            </Button>,
            <div className="m-4">Time Remaining: {formatTime(time)}</div>,
          ]}
        />
      )}

      {status === 1 && (
        <Result
          status="success"
          title={`Successfully Subscribed to Mcare ${plan?.description}`}
          subTitle="An email has been sent to kindly check and follow the steps to access benefits."
          extra={[
            <Button
              onClick={() => navigate("/main")}
              type="primary"
              key="home"
              style={{ backgroundColor: Colors.primaryBlue }}
            >
              OKAY
            </Button>,
          ]}
        />
      )}

      {status === 2 && (
        <Result
          status="info"
          title={`Your account is being processed for activation.`}
          subTitle="This may take up to an hour. A customer agent will contact you shortly for assistance."
          extra={[
            <Button
              onClick={() => navigate("/main")}
              type="primary"
              key="home"
              style={{ backgroundColor: Colors.primaryBlue }}
            >
              Go Home
            </Button>,
            // <Button onClick={() => navigate("/sign-in")} key="signin">
            //   Sign In
            // </Button>,
          ]}
        />
      )}
      {contextHolder}
    </div>
  );
}
